import { useState } from "react";
import { ArrowLeft, AlertTriangle, Send } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { useSendAdminNotification } from "../hooks/useNotifications";
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABEL_KEYS,
  DEEP_LINK_DESTINATIONS,
  MAX_BROADCAST_RECIPIENTS,
} from "../constants";
import type { NotificationType } from "../types";

function parseUserIds(raw: string): string[] {
  return raw
    .split(/[\n,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

interface BroadcastFormProps {
  onBack: () => void;
}

export function BroadcastForm({ onBack }: BroadcastFormProps) {
  const { t } = useLocale();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState<NotificationType>("GENERAL");
  const [userIdsRaw, setUserIdsRaw] = useState("");
  const [destinationId, setDestinationId] = useState("none");
  const [entityId, setEntityId] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [sendNotification, { loading: sending }] = useSendAdminNotification();

  const destination = DEEP_LINK_DESTINATIONS.find((d) => d.id === destinationId)!;
  const userIds = parseUserIds(userIdsRaw);
  const recipientCount = userIds.length;

  const isValid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    recipientCount > 0 &&
    recipientCount <= MAX_BROADCAST_RECIPIENTS &&
    (!destination.requiresId || entityId.trim().length > 0);

  const buildDataJson = (): string | undefined => {
    if (destinationId === "none") return undefined;
    const data: Record<string, string> = { screen: destination.screen };
    if (destination.requiresId && entityId.trim()) {
      data.id = entityId.trim();
    }
    return JSON.stringify(data);
  };

  const handleSend = async () => {
    setShowConfirm(false);
    setConfirmText("");
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await sendNotification({
        variables: {
          input: {
            userIds,
            type,
            title: title.trim(),
            body: body.trim(),
            data: buildDataJson(),
          },
        },
      });
      setSuccessMessage(
        t("notifications.broadcast.queuedToast", { count: recipientCount.toLocaleString() }),
      );
      setTitle("");
      setBody("");
      setUserIdsRaw("");
      setEntityId("");
      setDestinationId("none");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("notifications.broadcast.sendFailed");
      setErrorMessage(message);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("notifications.common.backToTemplates")}
      </button>

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">{t("notifications.broadcast.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("notifications.broadcast.description", {
            max: MAX_BROADCAST_RECIPIENTS.toLocaleString(),
          })}
        </p>
      </div>

      {/* Warning banner */}
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600 dark:text-yellow-400" />
        <p className="text-sm text-yellow-800 dark:text-yellow-300">
          <span className="font-medium">{t("notifications.broadcast.warningStrong")}</span>{" "}
          {t("notifications.broadcast.warningText")}
        </p>
      </div>

      {/* Success / Error messages */}
      {successMessage && (
        <div className="mb-4 rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3">
          <p className="text-sm font-medium text-green-700 dark:text-green-400">{successMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-sm font-medium text-destructive">{errorMessage}</p>
        </div>
      )}

      <div className="max-w-3xl space-y-5 rounded-lg border border-border bg-card p-6">
        {/* Recipients */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t("notifications.broadcast.recipientsLabel")}
          </label>
          <textarea
            value={userIdsRaw}
            onChange={(e) => setUserIdsRaw(e.target.value)}
            rows={4}
            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder={t("notifications.broadcast.recipientsPlaceholder")}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {recipientCount > 0 ? (
              <span className={recipientCount > MAX_BROADCAST_RECIPIENTS ? "font-medium text-destructive" : ""}>
                {t("notifications.broadcast.recipientCount", {
                  count: recipientCount.toLocaleString(),
                })}
                {recipientCount > MAX_BROADCAST_RECIPIENTS &&
                  t("notifications.broadcast.exceedsLimit", {
                    max: MAX_BROADCAST_RECIPIENTS.toLocaleString(),
                  })}
              </span>
            ) : (
              t("notifications.broadcast.separateHint")
            )}
          </p>
        </div>

        {/* Notification Type */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.broadcast.typeLabel")}</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as NotificationType)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {NOTIFICATION_TYPES.map((nt) => (
              <option key={nt} value={nt}>{t(NOTIFICATION_TYPE_LABEL_KEYS[nt])}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("notifications.broadcast.typeHint")}
          </p>
        </div>

        {/* Title */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.title")}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder={t("notifications.broadcast.titlePlaceholder")}
          />
        </div>

        {/* Body */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.body")}</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder={t("notifications.broadcast.bodyPlaceholder")}
          />
        </div>

        {/* Tap Destination */}
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.broadcast.tapDestinationLabel")}</label>
            <select
              value={destinationId}
              onChange={(e) => { setDestinationId(e.target.value); setEntityId(""); }}
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {DEEP_LINK_DESTINATIONS.map((d) => (
                <option key={d.id} value={d.id}>{t(d.labelKey)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("notifications.broadcast.tapDestinationHint")}
            </p>
          </div>

          {destination.requiresId && (
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t(destination.idLabelKey ?? "")} *
              </label>
              <input
                type="text"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={destination.idPlaceholder}
              />
            </div>
          )}
        </div>

        {/* Payload Preview */}
        <div className="border-t border-border pt-4">
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("notifications.broadcast.payloadPreview")}
          </h4>
          <div className="rounded bg-muted p-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-muted-foreground">{t("notifications.broadcast.previewType")}</span>
              <span className="font-medium text-foreground">{t(NOTIFICATION_TYPE_LABEL_KEYS[type])}</span>
              <span className="text-muted-foreground">{t("notifications.broadcast.previewTitle")}</span>
              <span className="font-medium text-foreground">
                {title || <span className="italic text-muted-foreground">{t("notifications.broadcast.previewEmpty")}</span>}
              </span>
              <span className="text-muted-foreground">{t("notifications.broadcast.previewBody")}</span>
              <span className="truncate font-medium text-foreground">
                {body || <span className="italic text-muted-foreground">{t("notifications.broadcast.previewEmpty")}</span>}
              </span>
              <span className="text-muted-foreground">{t("notifications.broadcast.previewDestination")}</span>
              <span className="font-medium text-foreground">{t(destination.labelKey)}</span>
              {buildDataJson() && (
                <>
                  <span className="text-muted-foreground">{t("notifications.broadcast.previewDataJson")}</span>
                  <code className="font-mono text-[11px] text-muted-foreground">{buildDataJson()}</code>
                </>
              )}
              <span className="text-muted-foreground">{t("notifications.broadcast.previewRecipients")}</span>
              <span className="font-medium text-foreground">{recipientCount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pt-2">
          {!showConfirm ? (
            <button
              type="button"
              onClick={() => setShowConfirm(true)}
              disabled={!isValid || sending}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {t("notifications.broadcast.reviewAndSend")}
            </button>
          ) : (
            <div className="w-full space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
              <p className="text-sm text-destructive">
                {t("notifications.broadcast.confirmPrompt", {
                  count: recipientCount.toLocaleString(),
                })}
              </p>
              <div>
                <label className="mb-1 block text-xs font-medium text-destructive">
                  {t("notifications.broadcast.typeToConfirm")}
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="h-9 w-48 rounded-md border border-destructive/50 bg-background px-3 text-sm text-foreground focus:border-destructive focus:outline-none focus:ring-1 focus:ring-destructive"
                  placeholder="SEND"
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || confirmText !== "SEND"}
                  className="inline-flex h-9 items-center rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sending ? t("notifications.broadcast.sending") : t("notifications.broadcast.confirmSend")}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
